import type {
  OpticalMeasureSettings,
  OpticalMeasureSettingsKeys,
} from '../src/ExpoOpticalMeasure.types'

/**
 * Frozen settings snapshots for the two SH tunings, so every default this
 * consistency work moves stays one tap away from its old value
 * (SH_CONSISTENCY_PLAN, Phase 0).
 *
 * `1.1` is the tuning shipped in app version 1.1; `1.2` is what the defaults
 * currently are. Both are **literals on purpose**: deriving either one from
 * `getOpticalMeasureSettings()` would make it track the very defaults it exists
 * to contrast with, and the A/B would silently compare a tuning against itself.
 * `shSettingsBaseline.test.ts` pins `SH_BASELINE_1_2` against the shipped
 * defaults instead, which catches the drift without hiding it.
 *
 * Adding a knob to the plan means adding it to `SH_PLAN_CHANGE_PATHS` and to
 * both columns in the same commit — the test fails otherwise.
 */
export type ShSettingsBaseline = '1.1' | '1.2'

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends Record<string, unknown>
    ? DeepPartial<T[K]>
    : T[K]
}

export type ShSettingsBaselineSnapshot = DeepPartial<OpticalMeasureSettings>

/**
 * Every leaf the plan touches, whether or not its two values differ yet. Typed
 * against the settings tree, so a renamed setting breaks the build here rather
 * than leaving the snapshot quietly describing a path that no longer exists.
 *
 * Rows whose columns still match are listed all the same: the snapshot is meant
 * to be a complete description of the 1.1 → 1.2 delta, not a partial one.
 */
export const SH_PLAN_CHANGE_PATHS: readonly OpticalMeasureSettingsKeys[] = [
  'framing.normal.guidanceDistanceToleranceCm',
  'framing.normal.captureDistanceToleranceCm',
  'framing.vbox.guidanceDistanceToleranceCm',
  'framing.vbox.captureDistanceToleranceCm',
  'framing.faceFit.ovalPaddingFactor',
  'framing.position.guidanceToleranceXFactor',
  'framing.position.guidanceToleranceYFactor',
  'framing.position.captureToleranceXFactor',
  'framing.position.captureToleranceYFactor',
  'framing.position.guidanceSizeToleranceFactor',
  'framing.position.captureSizeToleranceFactor',
  'geometry.rotationXOffsetDeg',
  'sh.gates.maxDifferenceMm',
  'sh.gates.maxRotationXDeg',
  'sh.gates.maxRotationYDeg',
  'sh.capture.targetCandidateFrames',
  'sh.capture.finalFrameMedianWeight',
  'sh.capture.spreadMetric',
  'sh.capture.maxSampleSpreadMm',
  'sh.capture.maxSampleDispersionMm',
  'sh.correction.includeHeadPitch',
  'sh.correction.lensDepthMm',
  'sh.scoring.qualityDistanceToleranceCm',
]

/** The tuning shipped in 1.1. Never derive this — see the type doc above. */
export const SH_BASELINE_1_1 = {
  framing: {
    normal: {
      guidanceDistanceToleranceCm: 15,
      captureDistanceToleranceCm: 20,
    },
    vbox: {
      guidanceDistanceToleranceCm: 15,
      captureDistanceToleranceCm: 20,
    },
    faceFit: {
      ovalPaddingFactor: 1.1,
    },
    position: {
      guidanceToleranceXFactor: 0.15,
      guidanceToleranceYFactor: 0.15,
      captureToleranceXFactor: 0.25,
      captureToleranceYFactor: 0.25,
      guidanceSizeToleranceFactor: 0.15,
      captureSizeToleranceFactor: 0.25,
    },
  },
  geometry: {
    rotationXOffsetDeg: 0,
  },
  sh: {
    gates: {
      maxDifferenceMm: 2.5,
      maxRotationXDeg: 8,
      maxRotationYDeg: 8,
    },
    capture: {
      targetCandidateFrames: 5,
      finalFrameMedianWeight: 2,
      spreadMetric: 'range',
      maxSampleSpreadMm: 1.5,
      // Unread under `range`, so inert at its 1.1 sibling values — pinned only
      // to keep the snapshot a complete description of the delta.
      maxSampleDispersionMm: 0.6,
    },
    correction: {
      includeHeadPitch: true,
      // 1.1 predates the vertex-parallax term entirely.
      lensDepthMm: 0,
    },
    scoring: {
      qualityDistanceToleranceCm: 20,
    },
  },
} as const satisfies ShSettingsBaselineSnapshot

/**
 * What the defaults are now. Promotions land here and in
 * `ExpoOpticalMeasure.settings.ts` in the same commit; the test that compares
 * the two is what keeps that honest.
 */
export const SH_BASELINE_1_2 = {
  framing: {
    // B4: a band-shaped prompt goes quiet inside itself, so subjects settle on
    // its near edge rather than the target — 24-26 cm against 35 cm. Paired
    // with distance guidance staying live through sampling.
    normal: {
      guidanceDistanceToleranceCm: 6,
      captureDistanceToleranceCm: 7,
    },
    vbox: {
      guidanceDistanceToleranceCm: 6,
      captureDistanceToleranceCm: 7,
    },
    faceFit: {
      // 1.1 put the oval's implied distance at 35/1.1 = 31.8 cm, 3.2 cm inside
      // the band's target — and the oval wins, being the dominant cue. 0.95
      // overshoots deliberately, because filling is not exact at range.
      ovalPaddingFactor: 0.95,
    },
    position: {
      guidanceToleranceXFactor: 0.15,
      guidanceToleranceYFactor: 0.15,
      captureToleranceXFactor: 0.25,
      captureToleranceYFactor: 0.25,
      guidanceSizeToleranceFactor: 0.15,
      captureSizeToleranceFactor: 0.25,
    },
  },
  geometry: {
    // Recentres the pose gate on the pose subjects hold at 35 cm with the
    // device at eye level: measured -6.1 deg true, sitting mid-window rather
    // than against an edge, which is what says the gate is not forcing it.
    rotationXOffsetDeg: 6,
  },
  sh: {
    gates: {
      maxDifferenceMm: 2.5,
      // Pitch is worth 0.38 mm/deg, so ±8 left 6 mm of headroom. ±3 around an
      // offset of 6 accepts a true pitch of −9..−3 and drops the chin-down
      // half, which both over-reads and detects more easily.
      maxRotationXDeg: 3,
      maxRotationYDeg: 8,
    },
    capture: {
      // A1: 1.34x tighter aggregate for ~1-2 s more hold time.
      targetCandidateFrames: 9,
      // A3: the frozen frame stops counting double in the median.
      finalFrameMedianWeight: 1,
      // B1a: a range gate tightens itself as A1 raises the sample count, so
      // the verdict moves to an n-independent statistic.
      spreadMetric: 'mad',
      maxSampleSpreadMm: 1.5,
      maxSampleDispersionMm: 1.0,
    },
    correction: {
      includeHeadPitch: true,
      // Ships off: the correction is measured but moves the absolute value,
      // which has no ground truth behind it. Opt in from the Pitch Correction
      // row.
      lensDepthMm: 0,
    },
    scoring: {
      qualityDistanceToleranceCm: 7,
    },
  },
} as const satisfies ShSettingsBaselineSnapshot

const SNAPSHOTS = {
  '1.1': SH_BASELINE_1_1,
  '1.2': SH_BASELINE_1_2,
} as const satisfies Record<ShSettingsBaseline, ShSettingsBaselineSnapshot>

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const mergeDeep = (
  target: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> =>
  Object.entries(patch).reduce<Record<string, unknown>>(
    (merged, [key, value]) => {
      const current = merged[key]
      return {
        ...merged,
        [key]:
          isPlainObject(value) && isPlainObject(current)
            ? mergeDeep(current, value)
            : value,
      }
    },
    { ...target }
  )

/** Reads a dot path, or `undefined` when any segment is missing. */
export const readShSettingsPath = (source: unknown, path: string): unknown =>
  path
    .split('.')
    .reduce<unknown>(
      (value, key) => (isPlainObject(value) ? value[key] : undefined),
      source
    )

/**
 * Every leaf path a snapshot actually writes, in `a.b.c` form. Takes a plain
 * record rather than the snapshot type so it can recurse into its own branches.
 */
export const shSettingsBaselinePaths = (
  snapshot: Record<string, unknown>
): string[] =>
  Object.entries(snapshot).flatMap(([key, value]) =>
    isPlainObject(value)
      ? shSettingsBaselinePaths(value).map((path) => `${key}.${path}`)
      : [key]
  )

/**
 * The subset of `SH_PLAN_CHANGE_PATHS` whose two columns disagree — the only
 * paths that can tell the baselines apart. Derived from the frozen literals
 * rather than declared, so promoting a default cannot leave it stale.
 */
export const SH_BASELINE_DIFFERING_PATHS = SH_PLAN_CHANGE_PATHS.filter(
  (path) =>
    readShSettingsPath(SH_BASELINE_1_1, path) !==
    readShSettingsPath(SH_BASELINE_1_2, path)
)

/** Deep-merges a snapshot over a settings object; other paths are untouched. */
export const applyShSettingsBaseline = (
  settings: OpticalMeasureSettings,
  baseline: ShSettingsBaseline
): OpticalMeasureSettings =>
  mergeDeep(settings, SNAPSHOTS[baseline]) as OpticalMeasureSettings
